// ============================================================================
// Bank reconciliation v2 — decision queue API contract (ALC-4235)
// ============================================================================
//
// Shared by the web screen (`/reconciliation/queue`) and the API/MCP adapters
// under `/reconciliation/v2/*`. Source of truth for product rules:
// `docs/specs/recon-v2/DECISIONS.md` (wins over `SPEC.md`).
//
// Conventions:
// - Money is a decimal **string**, signed: DEBIT (money out) negative, CREDIT positive,
//   in the `currencyCode` of the object that carries it unless the field says otherwise.
// - Local dates are `YYYY-MM-DD`; instants are ISO-8601 timestamps; a period is `YYYY-MM`.
// - Enum values below are the wire values. Prisma enums added for this epic must use the
//   same spelling so the adapters stay a pass-through.

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const RECON_V2_DECISION_GROUPS = ['CONFIRM', 'RESOLVE_DIFF', 'REVIEW', 'IDENTIFY'] as const;
export type ReconV2DecisionGroup = (typeof RECON_V2_DECISION_GROUPS)[number];

export const RECON_V2_CRITERION_KEYS = [
  'COUNTERPARTY',
  'AMOUNT',
  'DATE',
  'REFERENCE',
  'METHOD',
] as const;
export type ReconV2CriterionKey = (typeof RECON_V2_CRITERION_KEYS)[number];

export type ReconV2CriterionState = 'ok' | 'delta' | 'fail';

export const RECON_V2_CONTROL_SIGNALS = [
  'PAYMENT_BEFORE_INVOICE',
  'PAYMENT_BEFORE_APPROVAL',
  'DIRECTION_MISMATCH',
  'POSSIBLE_DUPLICATE',
] as const;
export type ReconV2ControlSignal = (typeof RECON_V2_CONTROL_SIGNALS)[number];

export const RECON_V2_PATTERN_KINDS = [
  'TRANSFER_PAIR',
  'CNPJ_CLUSTER',
  'INSTALLMENT',
  'FEE_LIKE',
] as const;
export type ReconV2PatternKind = (typeof RECON_V2_PATTERN_KINDS)[number];

/** Who decided (or proposed) a reconciliation. Mirrors `ReconciliationGroup.method`. */
export type ReconV2Source = 'RULES' | 'HISTORICAL' | 'AI' | 'MANUAL';

/** Header switch: bank accounts or credit cards. */
export type ReconV2AccountKind = 'BANK' | 'CARD';

export type ReconV2AccountState = 'TO_EXPLAIN' | 'STALE_STATEMENT' | 'READY_TO_CLOSE' | 'CLOSED';

export type ReconV2Direction = 'DEBIT' | 'CREDIT';

export type ReconV2TargetType =
  'AP_BILL' | 'AP_PAYMENT' | 'AR_INVOICE' | 'BANK_TX' | 'AP_REIMBURSEMENT' | 'CARD_STATEMENT';

/**
 * Adjustment types the v2 screen can send. The first twelve exist today in the Prisma
 * `AdjustmentType` enum; PIS/COFINS/CSLL/TARIFA_ADQUIRENCIA/DESCONTO_INCONDICIONAL are
 * appended by the schema slice. "CSRF" is a UI shortcut that expands into PIS + COFINS + CSLL.
 */
export const RECON_V2_ADJUSTMENT_TYPES = [
  'TARIFA',
  'JUROS',
  'MULTA',
  'DESCONTO',
  'IOF',
  'IRRF',
  'INSS',
  'ISS',
  'FX',
  'ARREDONDAMENTO',
  'ADIANTAMENTO',
  'DEVOLUCAO_ADIANTAMENTO',
  'PIS',
  'COFINS',
  'CSLL',
  'TARIFA_ADQUIRENCIA',
  'DESCONTO_INCONDICIONAL',
] as const;
export type ReconV2AdjustmentType = (typeof RECON_V2_ADJUSTMENT_TYPES)[number];

export type ReconV2Treatment = 'SETTLEMENT' | 'ADVANCE' | 'INTERNAL_APPLICATION';

// ---------------------------------------------------------------------------
// GET /reconciliation/v2/accounts?entityId=&period=&kind=
// ---------------------------------------------------------------------------

export interface ReconV2AccountsQuery {
  entityId: string;
  period: string;
  kind: ReconV2AccountKind;
}

export interface ReconV2AccountTab {
  accountId: string;
  kind: ReconV2AccountKind;
  institutionName: string;
  /** Short badge label, e.g. `SAN`, `BTG`. */
  institutionShortLabel: string;
  /** e.g. `****3688`. */
  maskedNumber: string | null;
  currencyCode: string;
  state: ReconV2AccountState;
  /** Movements (not lines) still in the queue for the period; ignores search/filters. */
  pendingCount: number;
  /**
   * ALC-4363 r1 (ORCH Q15): bank lines the provider removed that the ERP kept with active posted
   * GL, cumulative to the period end (each blocks the close). Separate from `pendingCount`, which
   * stays the queue's definition of pending (D-S4b); the account tab adds the two so it never
   * reads "0 a explicar" next to a blocked close. Absent on an older server.
   */
  providerRemovedCount?: number;
  /** Local date of the last statement data when `state = STALE_STATEMENT`. */
  staleSince: string | null;
  /** `BankStatement` for the period, when one exists (close/reopen target). */
  statementId: string | null;
  closedAt: string | null;
}

export type ReconV2PeriodStatusKind = 'CLOSED' | 'OPEN_ITEMS' | 'IN_PROGRESS' | 'CURRENT';

export interface ReconV2PeriodStatus {
  period: string;
  status: ReconV2PeriodStatusKind;
  /**
   * Movements in the queue across all accounts of the entity (for `OPEN_ITEMS`), plus (ALC-4363
   * r1, ORCH Q15) the removed-with-active-GL items that block this month's close: those dated in
   * the month or before it, so a later month never reads "0 em aberto" next to a blocked close.
   */
  openCount: number;
}

export interface ReconV2AccountsResponse {
  entity: { entityId: string; name: string; functionalCurrency: string };
  accounts: ReconV2AccountTab[];
  periods: ReconV2PeriodStatus[];
}

// ---------------------------------------------------------------------------
// GET /reconciliation/v2/entities
// ---------------------------------------------------------------------------

/**
 * One entity of the screen's header selector, under the same `op:read` the rest of the screen
 * needs (the tenant entity list needs `org:read`, which a reconciliation role may not hold).
 */
export interface ReconV2EntityListItem {
  entityId: string;
  name: string;
  /**
   * The one functional currency the entity's books agree on, or `null` when they do not state
   * one consistent, supported currency (no books, books that disagree). Never a default.
   */
  functionalCurrency: string | null;
}

export interface ReconV2EntitiesResponse {
  entities: ReconV2EntityListItem[];
}

// ---------------------------------------------------------------------------
// GET /reconciliation/v2/accounts/:accountId/proof?period=
// ---------------------------------------------------------------------------

/**
 * Where the statement balance of a period came from, in source order (OQ12 A, ALC-4360):
 * a balance a user typed from the PDF extrato (`USER_DECLARED`) outranks every importer-derived
 * figure; only a provider-stated `OPEN_FINANCE` daily balance at the same date outranks it.
 */
export type ReconV2StatementBalanceSource =
  'USER_DECLARED' | 'STATEMENT_CLOSING' | 'DAILY_BALANCE' | 'LAST_TRANSACTION_BALANCE';

export type ReconV2ProofBlocker =
  | 'QUEUE_NOT_EMPTY'
  | 'DIFFERENCE_NOT_ZERO'
  | 'UNEXPLAINED_RESIDUAL'
  | 'UNREMEASURED_FX'
  | 'STATEMENT_BALANCE_MISSING'
  | 'BANK_GL_ACCOUNT_UNMAPPED'
  | 'FX_RATE_MISSING'
  | 'ALREADY_CLOSED'
  /**
   * The month has not ended yet (decision D-S6c, ALC-4242). A running month has no final
   * statement balance and can still receive movements, so closing it would freeze a figure
   * the bank has not stated. Appended to the union rather than declared in the ALC-4242
   * block below because `ReconV2ProofBlocker` is a hand-written literal union.
   */
  | 'PERIOD_IN_PROGRESS'
  /**
   * Decision D-S6e (round 1): the statement data behind the proof is stale — no balance at
   * the period end, or an Open Finance connection that stopped delivering. The comparison is
   * then made against a figure the bank has since moved on from, so the close is refused
   * even when every other term happens to net to zero.
   */
  | 'STATEMENT_STALE'
  /**
   * ALC-4363 (decision ORCH Q11 = A): the provider removed a bank line that the ERP had to keep
   * because it still carries active posted GL. The bank no longer has the line and the ledger
   * still does, so the month cannot close until each line is resolved: the reconciliation undone
   * (which reverses the GL) or, for a movement journal, the journal reversed. Blocks even when `providerRemovedActiveGlSum` makes the identity net to zero: naming the
   * difference is not resolving it. Counts this period and earlier ones, like `QUEUE_NOT_EMPTY`.
   */
  | 'PROVIDER_REMOVED_ACTIVE_GL';

export interface ReconV2FxConversion {
  fromCurrency: string;
  toCurrency: string;
  rate: string;
  rateDate: string;
  /** e.g. `PTAX`, `BANXICO_FIX` — as returned by the currency policy module. */
  rateSource: string;
}

/**
 * The newest bank balance the system holds on or before the period end, compared with the ledger
 * at THAT SAME DATE (OQ19 A, ALC-4370). Filled only when the month-end statement is stale or
 * missing (`state = STALE_STATEMENT`); it is what the proof strip shows instead of "—" so the
 * user can see how far apart bank and ledger are. It is NOT the month-end proof: `statement`,
 * `difference`, the blockers and the close gate do not read it.
 *
 * Every figure is cut at `asOf` (inclusive): the ledger excludes journals dated after it and the
 * pending sum excludes movements the bank posted after it. Comparing a 22/09 bank figure with a
 * 30/09 ledger (option B) is rejected on purpose.
 */
export interface ReconV2LastAvailableBalance {
  /** Functional currency; null when a foreign-currency balance has no CLOSING rate at `asOf`. */
  amount: string | null;
  amountAccountCurrency: string;
  /** Chosen by the same source order as `statement.source`; ties on one date break by it. */
  source: ReconV2StatementBalanceSource;
  /** The balance's own date (ISO `YYYY-MM-DD`): never a projected day, never the period end. */
  asOf: string;
  /** Who typed the balance and when; set only when `source` is `USER_DECLARED`. */
  declaredByUserId: string | null;
  declaredAt: string | null;
  /** Null when account currency = functional currency. */
  conversion: ReconV2FxConversion | null;
  /**
   * `gl_balance` of the bank's ledger account on `asOf`, functional currency, in the chart of the
   * accounting period that contains `asOf` (a chart version that took over later has other
   * account ids). Null when the account is unmapped, no accounting period contains `asOf`, or the
   * bank account has no mapping in that period's chart; never 0 for a missing one.
   */
  ledgerAtAsOf: string | null;
  /** `amount - ledgerAtAsOf`, functional currency; null when either side is null. */
  differenceAtAsOf: string | null;
  /**
   * Signed sum of the movements dated on or before `asOf` that are still in the queue today
   * (one reconciled since then is not in it), functional currency; null when a foreign-currency
   * account has no rate at `asOf`.
   */
  pendingAtAsOf: string | null;
  pendingCountAtAsOf: number;
  /**
   * ALC-4363 r1 (ORCH Q16): the `providerRemovedActiveGlSum` term at `asOf`, so that
   * `differenceAtAsOf` has the same explanation the month-end figure has. Cut on the line's own
   * date (`postedAt` on or before `asOf`); the ledger side is judged as of NOW (an undone line is
   * gone), because the as-of rule for the journal itself moved to ALC-4375. Functional currency;
   * null when a foreign-currency account has no rate at `asOf`.
   */
  providerRemovedAtAsOf: string | null;
  providerRemovedCountAtAsOf: number;
}

export interface ReconV2BalanceProof
  extends ReconV2BalanceProofStatementFacts, ReconV2ProviderRemovedProofFacts {
  available: boolean;
  /**
   * Set when `available = false`. Typed by ALC-4237 round 2 so a consumer can switch on it
   * exhaustively; the values are `RECON_V2_PROOF_UNAVAILABLE_REASONS`, declared at the end of
   * this file (a forward reference, since the union is additive to this interface).
   */
  unavailableReason: ReconV2ProofUnavailableReason | null;
  accountId: string;
  period: string;
  periodEnd: string;
  accountCurrency: string;
  functionalCurrency: string;
  statement: {
    /** Functional currency; null when no statement balance exists for the period end. */
    amount: string | null;
    amountAccountCurrency: string | null;
    source: ReconV2StatementBalanceSource | null;
    asOf: string | null;
    /**
     * Who typed the balance, by user id, and when (ISO instant). Both are set only when `source`
     * is `USER_DECLARED` (ALC-4360) and null for every other source, so a figure with a person
     * behind it can be told from an imported one, in the close dialog and in the close audit.
     */
    declaredByUserId: string | null;
    declaredAt: string | null;
    /** Null when account currency = functional currency. */
    conversion: ReconV2FxConversion | null;
  };
  /**
   * The last balance the system has when the month-end statement is stale or missing, compared
   * with the ledger at its own date (ALC-4370). Null for a healthy account and whenever the system
   * has no balance at all. Additive: `statement` and every field below are unchanged by it.
   */
  lastAvailable: ReconV2LastAvailableBalance | null;
  ledger: {
    /** `gl_balance.closing`, functional currency. */
    amount: string;
    glAccountId: string | null;
    glAccountCode: string | null;
    glAccountName: string | null;
    bookId: string | null;
    coaVersionId: string | null;
  };
  /**
   * statement − ledger, functional currency. Null when the statement balance is missing.
   * Identity (exact, to the cent): difference = pendingSignedSum + providerRemovedActiveGlSum
   * + unremeasuredFx.amount + unexplainedResidual.
   */
  difference: string | null;
  /**
   * Signed sum of the movements still in the queue, functional currency.
   *
   * Null when no CLOSING rate resolved for a foreign-currency account: there is no functional
   * figure at all, and `'0'` would read as "the queue nets to nothing". The screen shows "—".
   * (Widened from `string` by ALC-4237 round 2, at the orchestrator's direction.)
   */
  pendingSignedSum: string | null;
  /** Null for functional-currency accounts. Blocks close while non-zero (decision A). */
  unremeasuredFx: {
    amount: string;
    remeasurementRan: boolean;
    conversion: ReconV2FxConversion | null;
  } | null;
  /** "R$ X no razão sem movimento no extrato". Blocks close while non-zero. */
  unexplainedResidual: string | null;
  /** Every field is null when no CLOSING rate resolved — see `pendingSignedSum`. */
  volume: {
    /** Σ |amount| of reconciled movements in the period (system + user), functional currency. */
    explained: string | null;
    /** Σ |amount| of queued movements; ignored movements count in neither. */
    toExplain: string | null;
    /** explained / (explained + toExplain), by value; 0..1. */
    progress: number | null;
  };
  reconciledByMethod: { method: ReconV2Source; count: number }[];
  /** Count reconciled by the system (RULES + HISTORICAL + AI) — the "A pelo Alchemy" of the strip's "R de T conciliadas, A pelo Alchemy". */
  systemReconciledCount: number;
  state: ReconV2AccountState;
  closable: boolean;
  blockers: ReconV2ProofBlocker[];
}

// ---------------------------------------------------------------------------
// Candidate summary (shared by queue, decision panel and workbench)
// ---------------------------------------------------------------------------

export interface ReconV2CandidateSummary {
  targetType: ReconV2TargetType;
  targetId: string;
  /** e.g. `Conta a pagar`, `Conta a receber`, `Reembolso`. */
  kindLabel: string;
  /** e.g. `Fatura 71374158`, `NFS-e 1.204`. */
  reference: string;
  partyName: string | null;
  partyDocument: string | null;
  amount: string;
  openAmount: string;
  currencyCode: string;
  dueDate: string | null;
  issueDate: string | null;
  /**
   * Accounting competence of the document as a date (`YYYY-MM-DD`; the screen shows `MM/AAAA`):
   * an AP bill's `billCompetencyDate`, an AR invoice's `competencyDate`. Null for a transfer
   * counterpart and for any document that has none — the row then omits the part (ALC-4235).
   */
  competenceDate: string | null;
  /** Raw document status, e.g. `APPROVED`. */
  status: string | null;
  /** pt-BR label for the status pill, e.g. `Aprovada`. */
  statusLabel: string | null;
  /** In-app link for "Abrir". */
  href: string | null;
}

// ---------------------------------------------------------------------------
// GET /reconciliation/v2/queue?accountId=&period=&group=&q=&direction=
// ---------------------------------------------------------------------------

export interface ReconV2QueueQuery {
  accountId: string;
  period: string;
  group?: ReconV2DecisionGroup;
  /** Name, CNPJ/CPF or amount. */
  q?: string;
  direction?: ReconV2Direction;
}

export interface ReconV2QueueItem {
  transactionId: string;
  accountId: string;
  postedAt: string;
  amount: string;
  currencyCode: string;
  direction: ReconV2Direction;
  description: string;
  counterpartyName: string | null;
  counterpartyDocument: string | null;
  /** e.g. `PIX`, `BOLETO`, `TED`. */
  paymentMethod: string | null;
  decisionGroup: ReconV2DecisionGroup;
  detectedPattern: ReconV2PatternKind | null;
  patternKey: string | null;
  controlSignals: ReconV2ControlSignal[];
  /** Row subtitle, e.g. `Conta a pagar · Fatura 71374158`. */
  hint: string | null;
  /** Amount difference vs the best candidate (RESOLVE_DIFF), account currency. */
  delta: string | null;
  /** e.g. `multa + juros`, `retenções IRRF + CSRF`. */
  deltaLabel: string | null;
  bestCandidate: ReconV2CandidateSummary | null;
  proposalId: string | null;
  /**
   * ALC-4325 (S1-AC12): the INVESTMENT account the "Conta de investimento" picker pre-selects for
   * an Aplicação / Resgate on this (checking) account. The `meta.internalOperation.investmentAccountId`
   * of the latest RECONCILED INTERNAL group on the account; legacy fallback: the latest such group
   * whose other leg sits on an INVESTMENT account. Null when neither exists, or when the account is
   * itself an INVESTMENT account. Optional because only the API's queue and decision reads set it.
   */
  suggestedInvestmentAccountId?: string | null;
  /**
   * ALC-4326 (item 7): the importer's own classification of the movement
   * (`BankTransaction.internalOperationType`, e.g. `RESCUE`), raw, not the effective type a pick
   * would override. Null when the importer did not classify it. Optional because only the API's
   * queue and decision reads set it.
   */
  internalOperationType?: string | null;
  /**
   * ALC-4326 (item 7): the user's pick stamped on the movement by an internal operation
   * (`BankTransaction.internalOperationTypeConfirmed`, S1). Null when nobody picked.
   */
  internalOperationTypeConfirmed?: string | null;
  /**
   * ALC-4326 (item 7): the movement's date is on or before its entity's "Fim do histórico
   * importado" (the posting book's `historicalLedgerEndDate`), the same date source S0c's posting
   * guard and the K2b pre-flight read. False when no date is saved. Lets the screen offer the
   * historical action without the caller needing `gl:read`.
   */
  insideHistoricalLedger?: boolean;
}

/**
 * A bank line the provider removed that the ERP had to keep because it still carries active posted
 * GL (ALC-4363, decision ORCH Q11 = A). It is off the bank side everywhere (not in the queue
 * `items`, the tab counts, the Conciliados log, the pending sums or the statement balance) and is
 * listed here instead as a reconciling item (queue group "Removidas pelo banco", row "Removida em DD/MM"), with
 * the existing "Desfazer conciliação" action: the legacy `DELETE /reconciliation/groups/:groupId`.
 * After the undo the GL is reversed, the line stays removed, and the item and its blocker are gone.
 */
export interface ReconV2ProviderRemovedProofLine {
  transactionId: string;
  accountId: string;
  /** ISO instant of the line's posting date, like `ReconV2QueueItem.postedAt`. */
  postedAt: string;
  /** Signed like a queue item: a credit is positive, a debit negative. Account currency. */
  amount: string;
  currencyCode: string;
  direction: ReconV2Direction;
  description: string;
  counterpartyName: string | null;
  /** When the provider removed the line (ISO instant); null if the stamp is unreadable. */
  removedAt: string | null;
}

/** The line as the queue lists it: with the reconciliation to undo and whether it can be. */
export interface ReconV2ProviderRemovedItem extends ReconV2ProviderRemovedProofLine {
  /**
   * The reconciliation group the undo acts on. Null when the active GL comes from a journal no
   * group holds (a movement journal): there is nothing to undo from this screen, and `undoable`
   * is false with no `undoBlockedReason`.
   */
  groupId: string | null;
  /** Whether the undo would be accepted, by the rules the Conciliados log reads. */
  undoable: boolean;
  /** Why `undoable` is false when a group exists, as a stable code the web maps to copy. */
  undoBlockedReason: ReconV2UndoBlockedReason | null;
  /**
   * The month (`YYYY-MM`) to reopen, set exactly when `undoBlockedReason` is `STATEMENT_CLOSED`
   * (the closed bank statement month) or `ACCOUNTING_PERIOD_CLOSED` (the closed accounting period
   * of the line's active journal); null otherwise. The web shows it in the row text
   * "Reabra o extrato de julho para desfazer", so it never guesses the month from the line date.
   */
  undoBlockedMonth: string | null;
}

export interface ReconV2GroupSummary {
  group: ReconV2DecisionGroup;
  /** Movements, not lines; respects search and filters. */
  count: number;
  totalAbsAmount: string;
}

export interface ReconV2QueueResponse {
  items: ReconV2QueueItem[];
  groups: ReconV2GroupSummary[];
  tabs: {
    toExplain: number;
    reconciled: number;
    ignored: number;
    /**
     * ALC-4363 r1 (ORCH Q15): removed items with active GL, cumulative to the period end, the
     * same list as `providerRemovedItems`. Kept OUT of `toExplain` on purpose: `toExplain` is the
     * queue's definition of pending (D-S4b), shared with the account tab and the proof, and a
     * removed line is not a decision. The screen adds the two for the "A explicar" label, so a
     * month with only removed items never reads 0. Absent on an older server.
     */
    providerRemoved?: number;
  };
}

// ---------------------------------------------------------------------------
// GET /reconciliation/v2/transactions/:transactionId/decision
// ---------------------------------------------------------------------------

export interface ReconV2Criterion {
  key: ReconV2CriterionKey;
  state: ReconV2CriterionState;
  bankValue: string | null;
  docValue: string | null;
  /** e.g. `pagamento`. */
  bankQualifier: string | null;
  /** e.g. `vencimento`. */
  docQualifier: string | null;
}

export interface ReconV2InferredAdjustment {
  type: ReconV2AdjustmentType;
  /** Signed per the convention in DECISIONS.md: + raises what passes through the bank. */
  amount: string;
  /** e.g. `2% sobre R$ 1.241,90`. */
  basis: string | null;
  glAccountCode: string | null;
  glAccountName: string | null;
}

export interface ReconV2ControlSignalDetail {
  code: ReconV2ControlSignal;
  message: string;
  suggestedTreatment: string | null;
}

export interface ReconV2PairedTransaction {
  transactionId: string;
  accountId: string;
  accountLabel: string;
  postedAt: string;
  description: string;
  amount: string;
  currencyCode: string;
}

/**
 * One link the proposed candidate settles, at the amount the engine paired (Charlie Q-04).
 *
 * `candidate` is the proposal's largest document only; an N:1 proposal pairs several, and an
 * instalment proposal pairs one instalment of a document, not its open balance. Confirm, review,
 * resolve-difference, Identificar's preselection and batch confirm all submit THESE, verbatim.
 */
export interface ReconV2DecisionAllocation {
  /** What the write links: the AP instalment (`AP_PAYMENT`) for an instalment leg, else the document. */
  targetType: ReconV2TargetType;
  targetId: string;
  /** Exact decimal string, the engine leg's amount (legs on one target summed); never rounded. */
  allocatedAmount: string;
  /** The document the screen shows, preselects and rejects (the bill or invoice). */
  documentType: ReconV2TargetType;
  documentId: string;
  reference: string;
  partyName: string | null;
  /** The instalments the engine matched: AP payment ids or AR schedule ids; empty for a whole document. */
  installmentIds: string[];
  /**
   * Charlie r6b Q1: for an AR invoice reached through its instalments, each matched parcel at its
   * own leg amount (Σ = `allocatedAmount`). Sent back verbatim on the reconcile target so the
   * receipt credits these parcels, not the earliest open one. Absent for every other target.
   */
  arScheduleAllocations?: ReconV2ArScheduleAllocation[];
}

/** One AR parcel a reconcile target settles, at an exact decimal string (Charlie r6b Q1). */
export interface ReconV2ArScheduleAllocation {
  scheduleId: string;
  amount: string;
}

export interface ReconV2Decision {
  item: ReconV2QueueItem;
  rawDescription: string | null;
  /** Insight line, e.g. "Mesma linha digitável do boleto…". */
  why: string | null;
  source: ReconV2Source | null;
  candidate: ReconV2CandidateSummary | null;
  /**
   * Every link the proposed candidate settles (Q-04). Empty with no candidate, and for a partial
   * proposal (a leg this screen cannot represent): settling the showable part would settle less
   * than the engine paired.
   */
  allocations: ReconV2DecisionAllocation[];
  criteria: ReconV2Criterion[];
  criteriaMatched: number;
  /** Criteria with data on both sides; "N de M critérios batem" uses this. */
  criteriaEvaluated: number;
  /**
   * |movement| − Σ allocations for the proposed candidate, account currency.
   *
   * **Null** when the proposal carries a leg this screen cannot represent (a rail
   * expectation, a custody deposit, a debit note, a card item): the comparison would then be
   * measured over only part of what the engine actually paired, and a confident number is
   * worse than none. Widened from `string` by ALC-4240 round 3 so the panel, the queue row
   * and the workbench all say the same thing about the same proposal.
   */
  difference: string | null;
  inferredAdjustments: ReconV2InferredAdjustment[];
  controlSignals: ReconV2ControlSignalDetail[];
  pairedTransaction: ReconV2PairedTransaction | null;
  discardedSuggestions: {
    candidate: ReconV2CandidateSummary;
    reason: 'LOW_CRITERIA' | 'USER_REJECTED';
  }[];
}

// ---------------------------------------------------------------------------
// GET /reconciliation/v2/transactions/:transactionId/candidates?type=&q=&minAmount=&maxAmount=&limit=&cursor=
// ---------------------------------------------------------------------------

export interface ReconV2WorkbenchCandidate extends ReconV2CandidateSummary {
  /**
   * Movement amount − candidate open amount, account currency. **Null** for a proposal with
   * an unrepresentable leg, for the same reason `ReconV2Decision.difference` is — see there.
   */
  delta: string | null;
  /** Days from due date to movement date (negative = paid early). */
  dueInDays: number | null;
  tags: string[];
}

/** One page of suggestions: 25 unless the caller asks for more (ALC-4235, ORCH Q19). */
export const RECON_V2_CANDIDATE_PAGE_DEFAULT = 25;
/** The most one page of suggestions may carry. */
export const RECON_V2_CANDIDATE_PAGE_MAX = 100;

/**
 * The bounds of the amount slider: the smallest and largest **open** amount (absolute value) over
 * the documents the type and the search leave, **before** the amount filter is applied, so the
 * slider does not shrink to what it has just selected.
 */
export interface ReconV2CandidateAmountRange {
  min: string;
  max: string;
}

export interface ReconV2CandidatesResponse {
  /**
   * One page, in one stable order: the engine's proposals first, then every other open document
   * by how close its open amount is to the movement (`|open − movement|` ascending), then due
   * date ascending, then id.
   */
  candidates: ReconV2WorkbenchCandidate[];
  /**
   * How many documents match the type, the search and the amount filter, proposals included and
   * each document once — the real count, not the length of the page.
   */
  total: number;
  /** Opaque. Send it back as `cursor` for the next page; null on the last page. */
  nextCursor: string | null;
  /** Null when no document matches the type and the search. */
  amountRange: ReconV2CandidateAmountRange | null;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** POST /reconciliation/v2/transactions/:transactionId/reconcile — header `Idempotency-Key`. */
export interface ReconV2ReconcileRequest {
  targets: {
    targetType: ReconV2TargetType;
    targetId: string;
    allocatedAmount: string;
    partial?: boolean;
    /**
     * AR_INVOICE only: the parcels this amount settles (Charlie r6b Q1). Validated against the
     * invoice's open parcels before anything is written; omitted, the receipt settles FIFO.
     */
    arScheduleAllocations?: ReconV2ArScheduleAllocation[];
  }[];
  /**
   * Widened in place by ALC-4243 from the narrow `{ type, amount, description? }` shape to the
   * full payload ALC-4241 declared. A forward reference: `ReconV2AdjustmentInput` is declared in
   * the ALC-4241 block at the end of this file, and every field it adds beyond `type` and
   * `amount` is optional — so a request already built against the narrow shape still compiles and
   * still behaves identically. Widened here rather than in an additions block because
   * declaration merging cannot restate a property with a different type.
   */
  adjustments: ReconV2AdjustmentInput[];
  treatment: ReconV2Treatment;
  transferPairId?: string;
  proposalId?: string;
}

export interface ReconV2ReconcileResponse {
  groupId: string;
  transactionIds: string[];
  journalIds: string[];
}

/** POST /reconciliation/v2/batch-confirm — every item becomes its own group; failures are per item. */
export interface ReconV2BatchConfirmRequest {
  transactionIds: string[];
}

export interface ReconV2BatchConfirmResponse {
  results: {
    transactionId: string;
    ok: boolean;
    groupId: string | null;
    errorCode: string | null;
  }[];
}

/** POST /reconciliation/v2/transactions/:transactionId/reject-candidate → recomputed ReconV2Decision. */
export interface ReconV2RejectCandidateRequest {
  targetType: ReconV2TargetType;
  targetId: string;
  note?: string;
}

/** POST /reconciliation/v2/transactions/:transactionId/ignore · DELETE same path restores. */
export interface ReconV2IgnoreRequest {
  reason: string;
}

/*
 * Undo has no v2 route (ALC-4244, owner decision 2026-09-27): the v2 screen calls the legacy
 * `DELETE /reconciliation/groups/:groupId` with `DeleteReconciliationGroupRequest`
 * (`./reconciliation-groups`). It answers 204 with no body and deletes the group — there is no
 * VOIDED history (follow-up F12).
 */

// ---------------------------------------------------------------------------
// GET /reconciliation/v2/log?accountId=&period=  ("Conciliados" tab)
// ---------------------------------------------------------------------------

export interface ReconV2LogItem {
  groupId: string;
  transactionId: string;
  postedAt: string;
  description: string;
  amount: string;
  currencyCode: string;
  decidedByName: string | null;
  decidedAt: string;
  undoable: boolean;
  /**
   * Why `undoable` is false, as a stable code the web maps to pt-BR (ALC-4244). Absent when the
   * group is undoable; optional so a client built before it still reads the log.
   */
  undoBlockedReason?: ReconV2UndoBlockedReason | null;
}

/**
 * The stable reasons an undo would be refused, read off the same rules the legacy DELETE runs:
 *
 * - `MOVEMENT_JOURNAL_POSTED` — a Rendimento / IR-IOF internal operation whose journal is posted on
 *   the bank movement itself; the DELETE answers `RECONCILIATION_UNDO_MOVEMENT_JOURNAL_POSTED`.
 * - `GL_POSTING_NOT_REVERSIBLE` — a posted journal the reversal cannot plan
 *   (`GROUP_HAS_LIVE_GL_POSTING`).
 * - `PARTY_BALANCE_CONSUMED` — a vendor advance / client-recoverable balance already used.
 * - `CREATED_LEG_LINKED_ELSEWHERE` — ALC-4326 (S2-AC3): the investment-side movement an
 *   Aplicação / Resgate created (route b) is now part of another reconciliation; the DELETE
 *   answers `RECONCILIATION_UNDO_CREATED_LEG_LINKED_ELSEWHERE`.
 * - `CREATED_LEG_PERIOD_CLOSED` — ALC-4326 (S2-AC3): that created movement sits in a closed
 *   month; the DELETE answers `RECON_V2_STATEMENT_CLOSED`.
 * - `STATEMENT_CLOSED` — ALC-4363 r1 (ORCH Q14 a): a bank movement of the group sits in a month
 *   whose v2 bank statement is closed; the DELETE answers `RECON_V2_STATEMENT_CLOSED`. Set only
 *   on a provider-removed item (`ReconV2ProviderRemovedItem.undoBlockedMonth` names the month to
 *   reopen); the Conciliados log never carries it.
 * - `ACCOUNTING_PERIOD_CLOSED` — ALC-4363 r1 (ORCH Q16): the accounting period of the line's
 *   active journal is closed while the statement is open, so the reversal would have to be dated
 *   in a closed period. Set only on a provider-removed item (`undoBlockedMonth` names the month
 *   whose books to reopen); the Conciliados log never carries it.
 */
export const RECON_V2_UNDO_BLOCKED_REASONS = [
  'MOVEMENT_JOURNAL_POSTED',
  'GL_POSTING_NOT_REVERSIBLE',
  'PARTY_BALANCE_CONSUMED',
  'CREATED_LEG_LINKED_ELSEWHERE',
  'CREATED_LEG_PERIOD_CLOSED',
  'STATEMENT_CLOSED',
  'ACCOUNTING_PERIOD_CLOSED',
] as const;
export type ReconV2UndoBlockedReason = (typeof RECON_V2_UNDO_BLOCKED_REASONS)[number];

export interface ReconV2LogGroup {
  /** Stable key: method + rule/pattern id. */
  key: string;
  who: ReconV2Source;
  /** e.g. `Tarifas Santander`. */
  title: string;
  /** e.g. `Regra "Tarifa PIX/TED fornecedor"`. */
  subtitle: string | null;
  ruleId: string | null;
  itemCount: number;
  netAmount: string;
  /** True for internal movements shown as volume (`vol.`), e.g. applications/redemptions. */
  volumeOnly: boolean;
  /** `ON_IMPORT` when auto-applied by the engine or a rule at ingestion. */
  when: 'ON_IMPORT' | 'BY_USER';
  /**
   * Present on a HISTORICAL_LEDGER row ("Já contabilizado no histórico importado", ALC-4323):
   * the person who confirmed it. The "Quem" column shows it instead of the source label; null
   * when the user no longer resolves.
   */
  decidedByName?: string | null;
  /**
   * True when a fleet agent made these reconciliations (method AI with a run and decision, ALC-4668,
   * D13): the screen shows an "Agent" label. Absent otherwise; agent rows never merge with other AI rows.
   */
  madeByAgent?: true;
  items: ReconV2LogItem[];
}

export interface ReconV2LogResponse {
  groups: ReconV2LogGroup[];
}

// ---------------------------------------------------------------------------
// Statement close / reopen
// ---------------------------------------------------------------------------

/** POST /reconciliation/v2/statements/:statementId/close — permission `reconciliation:close`. */
export interface ReconV2CloseStatementResponse {
  statementId: string;
  status: 'RECONCILED';
  closedAt: string;
  closedBy: string;
}

/** POST /reconciliation/v2/statements/:statementId/reopen — permission `reconciliation:reopen`. */
export interface ReconV2ReopenStatementRequest {
  reason: string;
}

export interface ReconV2ReopenStatementResponse {
  statementId: string;
  status: 'OPEN';
  reopenedAt: string;
  reopenedBy: string;
}

// ---------------------------------------------------------------------------
// --- ALC-4240 additions ---
// Queue read model (decision groups, criteria, candidates, reject-candidate).
// Interface re-declarations below merge with the declarations above; every added
// member is optional, so nothing that already builds a response stops compiling.
// ---------------------------------------------------------------------------

/**
 * Why a queue response carries no items at all, as opposed to "the period is clean".
 *
 * `CARD_NOT_SUPPORTED` mirrors the balance proof's reason in DECISIONS.md: card movements
 * live in `CardStatementItem`, which has no decision columns, so a card account has no
 * decision queue in the first delivery.
 */
export const RECON_V2_QUEUE_UNAVAILABLE_REASONS = ['CARD_NOT_SUPPORTED'] as const;
export type ReconV2QueueUnavailableReason = (typeof RECON_V2_QUEUE_UNAVAILABLE_REASONS)[number];

/** Hard cap on `ReconV2QueueResponse.items`. The contract has no cursor; `truncated` says so. */
export const RECON_V2_QUEUE_ITEM_CAP = 500;

export interface ReconV2QueueResponse {
  /** Null on a bank account; set when the account kind has no queue yet. */
  unavailableReason?: ReconV2QueueUnavailableReason | null;
  /**
   * True when more movements match the filters than `RECON_V2_QUEUE_ITEM_CAP` returned.
   * `groups` counts stay complete, so the screen can say "showing N of M".
   */
  truncated?: boolean;
  /**
   * ALC-4363: the lines the provider removed that the ERP kept with active posted GL, dated in the
   * period OR BEFORE IT (cumulative to the period end, ORCH Q15: the item blocks the close of every
   * later month, so it is listed in each of them), oldest first. Each carries its own `postedAt`,
   * which is how the screen tags an item that belongs to an earlier month. Reconciling items, not
   * queue work: they are outside `items`, `groups` and `tabs.toExplain`, and ignore the search and
   * the filters (an item the user filtered away would hide a month-close blocker).
   */
  providerRemovedItems?: ReconV2ProviderRemovedItem[];
}

/** `q` also matches an amount; this is how the API parses that third form. */
export const RECON_V2_QUEUE_SEARCH_KINDS = ['TEXT', 'DOCUMENT', 'AMOUNT'] as const;
export type ReconV2QueueSearchKind = (typeof RECON_V2_QUEUE_SEARCH_KINDS)[number];

/** Document families the Identificar workbench search can be narrowed to. */
export const RECON_V2_CANDIDATE_TYPES = ['AP', 'AR', 'TRANSFER', 'ALL'] as const;
export type ReconV2CandidateType = (typeof RECON_V2_CANDIDATE_TYPES)[number];

export interface ReconV2CandidatesQuery {
  type?: ReconV2CandidateType;
  /** Party name, CNPJ/CPF, document reference or amount. */
  q?: string;
  /**
   * Inclusive bounds on the document's **open** amount, absolute value (decimal strings such as
   * `1500.00`). A proposal obeys them too. `min` above `max` is a 400.
   */
  minAmount?: string;
  maxAmount?: string;
  /** Page size, 1 to `RECON_V2_CANDIDATE_PAGE_MAX`; `RECON_V2_CANDIDATE_PAGE_DEFAULT` when absent. */
  limit?: number;
  /** The `nextCursor` of the previous page. A cursor the API did not issue is a 400. */
  cursor?: string;
}

/** Why the engine will not offer a target for a movement again. */
export const RECON_V2_REJECT_REASONS = ['USER_REJECTED', 'LOW_CRITERIA'] as const;
export type ReconV2RejectReason = (typeof RECON_V2_REJECT_REASONS)[number];

/**
 * The target vocabulary as a runtime tuple. `ReconV2TargetType` above is a type-only union,
 * so every adapter that validates a request body had to restate the six members by hand —
 * and a restated list is one that drifts. Declared to equal the union, so adding a member
 * above without adding it here stops compiling.
 */
export const RECON_V2_TARGET_TYPES = [
  'AP_BILL',
  'AP_PAYMENT',
  'AR_INVOICE',
  'BANK_TX',
  'AP_REIMBURSEMENT',
  'CARD_STATEMENT',
] as const satisfies readonly ReconV2TargetType[];

/**
 * `satisfies` only proves every **listed** member is a valid target type; it says nothing
 * about a member of the union that the list forgot. This is the other half: adding a member
 * to `ReconV2TargetType` without adding it here stops compiling, which is what keeps the
 * DTO's runtime validation and the type in step. (Same `AssertNever` pattern ALC-4237 uses.)
 */
export type _ReconV2TargetTypesExhaustive = AssertNever<
  Exclude<ReconV2TargetType, (typeof RECON_V2_TARGET_TYPES)[number]>
>;
// --- ALC-4237 additions ---
//
// Runtime value arrays for the unions above, plus the fields the balance proof needs to state
// its answer honestly. CRITICAL RULE 10 requires the API DTOs (`@IsIn`) and the MCP tool
// (`z.enum`) to take their values from `@alinea/types` rather than redeclare them, and the
// union types they mirror are hand-written literal unions — so nothing derives one from the
// other automatically. The `satisfies` clause plus the exhaustiveness checks at the end of
// this block close that gap in both directions: a value the union does not allow fails to
// compile, and a union member no array lists fails to compile too.

export const RECON_V2_ACCOUNT_KINDS = [
  'BANK',
  'CARD',
] as const satisfies readonly ReconV2AccountKind[];

export const RECON_V2_ACCOUNT_STATES = [
  'TO_EXPLAIN',
  'STALE_STATEMENT',
  'READY_TO_CLOSE',
  'CLOSED',
] as const satisfies readonly ReconV2AccountState[];

export const RECON_V2_STATEMENT_BALANCE_SOURCES = [
  'USER_DECLARED',
  'STATEMENT_CLOSING',
  'DAILY_BALANCE',
  'LAST_TRANSACTION_BALANCE',
] as const satisfies readonly ReconV2StatementBalanceSource[];

export const RECON_V2_PROOF_BLOCKERS = [
  'QUEUE_NOT_EMPTY',
  'DIFFERENCE_NOT_ZERO',
  'UNEXPLAINED_RESIDUAL',
  'UNREMEASURED_FX',
  'STATEMENT_BALANCE_MISSING',
  'BANK_GL_ACCOUNT_UNMAPPED',
  'FX_RATE_MISSING',
  'ALREADY_CLOSED',
  // Appended last by ALC-4242 so the emit order of `computeReconV2Blockers` — which follows
  // this array — stays stable for the assertions written against it.
  'PERIOD_IN_PROGRESS',
  'STATEMENT_STALE',
  // Appended last by ALC-4363, for the same reason: the emit order follows this array.
  'PROVIDER_REMOVED_ACTIVE_GL',
] as const satisfies readonly ReconV2ProofBlocker[];

export const RECON_V2_SOURCES = [
  'RULES',
  'HISTORICAL',
  'AI',
  'MANUAL',
] as const satisfies readonly ReconV2Source[];

export const RECON_V2_PERIOD_STATUS_KINDS = [
  'CLOSED',
  'OPEN_ITEMS',
  'IN_PROGRESS',
  'CURRENT',
] as const satisfies readonly ReconV2PeriodStatusKind[];

/**
 * Why a balance proof carries no comparison. Set on `ReconV2BalanceProof.unavailableReason`
 * whenever `available = false`: the account exists and the caller may see it, but the ledger
 * side cannot be stated, so refusing to compare is the only honest answer.
 */
export const RECON_V2_PROOF_UNAVAILABLE_REASONS = [
  /** Cards are not supported in the first delivery (provisional; owner reviews at wave end). */
  'CARD_NOT_SUPPORTED',
  /**
   * The bank GL account is `RESTRICTED_FGA` and this principal holds no grant on it. The
   * ledger balance, code and name are withheld rather than reported as unmapped, which would
   * be a lie the user could act on.
   */
  'LEDGER_ACCOUNT_RESTRICTED',
  /**
   * Two or more bank accounts of the entity map to the same GL account (decision D-S2a), so
   * no per-account ledger balance exists. Summing them would invent a figure that reconciles
   * against nothing; `sharedLedgerAccountNames` says which accounts share it.
   */
  'LEDGER_SHARED_GL_ACCOUNT',
  /**
   * No accounting `Period` contains this month end — a month before the book's first period,
   * or a calendar that was never generated. There is no ledger balance to compare against, so
   * the proof must not report a difference of zero and call the account closable.
   */
  'LEDGER_PERIOD_NOT_FOUND',
  /**
   * The containing `Period` does not end on the calendar month end: a quarterly or 4-4-5 book
   * whose Q3 row also holds September's postings. Comparing August's statement against it
   * would charge the account for movements from another month. v1 refuses rather than
   * rebuilding the month from journal lines.
   */
  'LEDGER_PERIOD_NOT_MONTHLY',
] as const;

export type ReconV2ProofUnavailableReason = (typeof RECON_V2_PROOF_UNAVAILABLE_REASONS)[number];

// ---------------------------------------------------------------------------
// --- ALC-4241 additions ---
// Adjustment posting: the CSRF shortcut, the D2/D4 inputs the resolver reads, and the
// vocabulary the screen needs to explain a skipped or refused adjustment. Purely additive —
// `ReconV2ReconcileRequest.adjustments` keeps its existing shape, and a write that sends only
// `{ type, amount, description }` behaves exactly as before.
// ---------------------------------------------------------------------------

/**
 * UI shortcuts that are not adjustment types. A shortcut never reaches persistence: the write
 * boundary expands it first (`expandCsrfShortcut` in `@erp/services/reconciliation`).
 *
 * `CSRF` is one 4,65% figure on the document that must become three adjustments, because the
 * provider deducts per contribution (IN SRF 459 art. 7º §§1º-2º) and PIS, COFINS and CSLL each
 * have their own account.
 */
export const RECON_V2_ADJUSTMENT_SHORTCUTS = ['CSRF'] as const;
export type ReconV2AdjustmentShortcut = (typeof RECON_V2_ADJUSTMENT_SHORTCUTS)[number];

/** What the three components of a `CSRF` shortcut expand into, in DARF order. */
export const RECON_V2_CSRF_COMPONENTS = ['PIS', 'COFINS', 'CSLL'] as const;

/**
 * D2: how ISS withheld by the customer is treated on the AR side.
 *
 * The default is `OWN_LIABILITY` (orchestrator decision D-S5a): ISS withheld by the customer is
 * not an advance of the company's own tax, it IS the company's ISS being collected by the payer
 * as the responsible party (LC 116 art. 6º), so it writes down `ISS a recolher`.
 * `RECOVERABLE` stays explicitly selectable for municipalities that do not transfer the whole
 * responsibility.
 */
export type ReconV2IssTreatment = 'RECOVERABLE' | 'OWN_LIABILITY';

/** D2's default, in one place. */
export const RECON_V2_ISS_TREATMENT_DEFAULT: ReconV2IssTreatment = 'OWN_LIABILITY';

/**
 * The full adjustment payload. `ReconV2ReconcileRequest.adjustments` accepts this shape; every
 * field beyond `type` and `amount` is optional, so nothing already built against the request
 * needs to change.
 */
export interface ReconV2AdjustmentInput {
  type: ReconV2AdjustmentType | ReconV2AdjustmentShortcut;
  /** Signed: `+` raises what passes through the bank, `-` reduces it. */
  amount: string;
  description?: string;
  /**
   * D4: the user's own justification, required when the source document never declared this
   * withholding (or declared less than was withheld). It is **the user's text only** — never an
   * auto-generated description, which would make the rule vacuous.
   */
  note?: string;
  /** D2; defaults to `OWN_LIABILITY`. Only read for an AR `ISS` adjustment. */
  issTreatment?: ReconV2IssTreatment;
  /**
   * Which target in `targets` this adjustment belongs to. Required when the movement settles more
   * than one receivable: a withholding belongs to exactly one invoice, and pro-rating it across
   * invoices moves money between them.
   */
  targetId?: string;
}

/** Why an adjustment produced no journal. Shown in the reconciled log, never an error. */
export const RECON_V2_ADJUSTMENT_SKIP_REASONS = [
  /** The bill or invoice already booked this withholding; the open balance is already net. */
  'ALREADY_ON_DOCUMENT',
  /** The AP tax-liability rail recognises this tax at payment and owns the journal. */
  'POSTED_BY_PAYMENT_RETENTION_RAIL',
  'ZERO_AMOUNT',
  /** A partial-payment or overpayment remainder is an audit marker, not a rounding difference. */
  'NON_POSTING_SEMANTIC',
] as const;
export type ReconV2AdjustmentSkipReason = (typeof RECON_V2_ADJUSTMENT_SKIP_REASONS)[number];

/**
 * Stable refusal codes for adjustment posting. Classify by code, never by message.
 *
 * Declared here, as literals, because `@alinea/types` cannot import `@erp/services`: this is the
 * single source, and the services-side rejection union and the reconciliation GL-posting registry
 * both derive from it rather than re-declaring. The one pt-BR message map lives in
 * `packages/services/src/reconciliation/v2/errors.ts`.
 */
export const RECON_V2_ADJUSTMENT_ERROR_CODES = [
  'RECON_V2_ADJUSTMENT_UNSUPPORTED_COMBINATION',
  'RECON_V2_ADJUSTMENT_SIGN_INVALID',
  'RECON_V2_ADJUSTMENT_ROUNDING_TOLERANCE_EXCEEDED',
  'RECON_V2_ADJUSTMENT_JURISDICTION_MISMATCH',
  'RECON_V2_ADJUSTMENT_NOTE_REQUIRED',
  'RECON_V2_ADJUSTMENT_CSRF_AMOUNT_INVALID',
  'RECON_V2_ADJUSTMENT_SLOT_NOT_RESOLVED',
  /** An undifferentiated withholding total cannot be booked: it must be split by tax. */
  'RECON_V2_ADJUSTMENT_AGGREGATE_SPLIT_REQUIRED',
  /** A multi-invoice group left it ambiguous which receivable an adjustment belongs to. */
  'RECON_V2_ADJUSTMENT_ATTRIBUTION_REQUIRED',
  /** Σ of the receipt legs did not equal the bank movement. Never write a journal on this. */
  'RECON_V2_ADJUSTMENT_CASH_MISMATCH',
  /** The slot already has a mapping that this posting must not silently repoint. */
  'RECON_V2_ADJUSTMENT_SLOT_MAPPING_CONFLICT',
  /**
   * A stored adjustment amount could not be read as a number at all. Never treated as zero: a
   * value nobody can parse is a data error the user has to see, and silently posting nothing for
   * it would leave the movement short by an amount no one can account for.
   */
  'RECON_V2_ADJUSTMENT_AMOUNT_UNREADABLE',
  // --- ALC-4369: an adjustment on a document the movement left no cash for ---
  /**
   * An adjustment (a retention above all) is attributed to a document whose allocation is 0,00.
   * That document received no cash, so nothing it carries can be explained by this movement:
   * posting it either drops the adjustment or debits the bank with money that never arrived.
   * Refused before any write; also the classified answer when a link carrying an adjustment
   * would otherwise be skipped as a zero-amount receipt.
   */
  'RECON_V2_ADJUSTMENT_TARGET_NO_CASH',
  /**
   * The adjustments attributed to one document are as large as, or larger than, what the
   * movement allocates to it (a retention of 150,00 on a document settled with 50,00). The
   * document would receive zero or negative cash, which no receipt can carry. Refused before
   * any write, and the classified answer when the receipt allocator meets the same arithmetic.
   */
  'RECON_V2_ADJUSTMENT_EXCEEDS_ALLOCATION',
] as const;
export type ReconV2AdjustmentErrorCode = (typeof RECON_V2_ADJUSTMENT_ERROR_CODES)[number];

/** D6: the AR rounding tolerance, exact. Compared with Decimal, never rounded. */
export const RECON_V2_AR_ROUNDING_TOLERANCE = '0.05';

/**
 * Stable API error codes for the accounts tab, the balance proof and adjustment posting.
 * Classified by code, never by message text.
 *
 * `RECON_V2_ADJUSTMENT_ERROR_CODES` is a named subset spread in here, declared **above** this
 * array on purpose: spreading a `const` that is declared later throws a TDZ error the moment the
 * module loads, which would take down every importer rather than failing a type-check.
 */
export const RECON_V2_ERROR_CODES = [
  'RECON_V2_PERIOD_INVALID',
  'RECON_V2_ENTITY_NOT_FOUND',
  'RECON_V2_ACCOUNT_NOT_FOUND',
  // ALC-4240 (decision queue): the movement is absent, or on an account the caller's bank
  // scope does not reach. One code for both, so the answer is not an existence oracle.
  'RECON_V2_TRANSACTION_NOT_FOUND',
  // ── Statement close / reopen (ALC-4242) ──
  /** 404 — no `BankStatement` with that id, or the caller cannot see its bank account. */
  'RECON_V2_STATEMENT_NOT_FOUND',
  /** 409 — the balance proof still reports blockers; `details.blockers` carries them. */
  'RECON_V2_STATEMENT_NOT_CLOSABLE',
  /** 409 — the statement is already closed (also the loser of a concurrent double close). */
  'RECON_V2_STATEMENT_ALREADY_CLOSED',
  /** 409 — reopen was asked for a statement that is not closed. */
  'RECON_V2_STATEMENT_NOT_CLOSED',
  /** 400 — reopen needs a justification of 1..1000 characters after trimming. */
  'RECON_V2_REOPEN_REASON_REQUIRED',
  /** 409 — a write was attempted into a month whose statement is closed (decision D-S6b). */
  'RECON_V2_STATEMENT_CLOSED',
  /**
   * 409 — the accounting period containing this month is CLOSED or LOCKED, so reopening the
   * statement would let bank-side writes land inside a closed GL month (ALC-4242 round 1).
   */
  'RECON_V2_GL_PERIOD_CLOSED',

  // ── Identificar suggestions paging (ALC-4235) ──
  /** 400 — the `cursor` is not one this API issued (or was cut off). Restart from page 1. */
  'RECON_V2_CANDIDATE_CURSOR_INVALID',
  /** 400 — `minAmount` / `maxAmount` is not a non-negative decimal, or min is above max. */
  'RECON_V2_CANDIDATE_AMOUNT_RANGE_INVALID',

  // ── Adjustment posting (ALC-4241, S5) ──
  ...RECON_V2_ADJUSTMENT_ERROR_CODES,
  // ALC-4243 (write actions). Literals, not a spread of a named subset: the subset would have to
  // be declared above this array, and a spread of a `const` declared BELOW it throws a TDZ error
  // the moment the module loads — see the note on `RECON_V2_ADJUSTMENT_ERROR_CODES`.
  //
  // Every one is refused at the write boundary, before anything is saved.
  /**
   * D-S5b: an AP withholding the bill never declared, or a bank fee, on an AP target. Those are
   * booked on the bill and matched by the engine respectively, so the queue has no journal to
   * write for them — and writing the match first and discovering it later (the AP adapter's old
   * silent `UNSUPPORTED_ADJUSTMENT_TYPE` path) leaves a reconciliation that explains nothing.
   */
  'RECON_V2_ADJUSTMENT_AP_FIX_ON_BILL',
  /** The `Idempotency-Key` header is absent on a write that requires it. */
  'RECON_V2_IDEMPOTENCY_KEY_REQUIRED',
  /** The same key was already used for a DIFFERENT body. Never a replay; always a caller bug. */
  'RECON_V2_IDEMPOTENCY_KEY_REUSED',
  /** A request with this key is still in flight. The caller retries; it is not a failure. */
  'RECON_V2_IDEMPOTENCY_IN_PROGRESS',
  /** More items than `RECON_V2_BATCH_CONFIRM_MAX` in one batch. */
  'RECON_V2_BATCH_TOO_LARGE',
  /** Per item: its S4 decision group is not `CONFIRM`, so batch confirm must not decide it. */
  'RECON_V2_BATCH_ITEM_NOT_CONFIRMABLE',
  /** `ReconV2IgnoreRequest.reason` was absent, blank once trimmed, or over the column's length. */
  'RECON_V2_IGNORE_REASON_REQUIRED',
  /** Restore was asked for a movement that is not ignored. */
  'RECON_V2_TRANSACTION_NOT_IGNORED',
  /** The movement already has a countable reconciliation group; it is not queue work. */
  'RECON_V2_TRANSACTION_ALREADY_RECONCILED',
  /** A `treatment` this slice does not implement. Never silently ignored. */
  'RECON_V2_TREATMENT_UNSUPPORTED',
  /** A create-and-reconcile `kind` with no server-side create flow behind it. */
  'RECON_V2_CREATE_KIND_UNSUPPORTED',
  /** The reconcile body named no target and no adjustment: there is nothing to reconcile. */
  'RECON_V2_RECONCILE_TARGETS_REQUIRED',
  /**
   * A batch item failed for a reason that carried no stable code of its own.
   *
   * The fallback exists so `ReconV2BatchConfirmResponse.errorCode` is never a message: a caller
   * branching on prose is a caller that breaks when the prose is translated. An item answering
   * this is one to open in the panel, where the underlying failure states itself.
   */
  'RECON_V2_BATCH_ITEM_FAILED',
  /** The movement is ignored. Restore it before deciding it again. */
  'RECON_V2_TRANSACTION_ALREADY_IGNORED',
  /** A `targetType` the v2 write cannot turn into a reconciliation link. */
  'RECON_V2_TARGET_TYPE_UNSUPPORTED',
  /** A create-and-reconcile body left out a field its own `kind` requires. */
  'RECON_V2_CREATE_INPUT_INVALID',
  // --- ALC-4243 review round 1. Literals again, for the TDZ reason above. ---
  /** The adjustment type does not exist on this side of the reconciliation. Refused pre-write. */
  'RECON_V2_ADJUSTMENT_TYPE_UNSUPPORTED',
  /** `attributeToTargetId` named a document this reconciliation does not settle. */
  'RECON_V2_ADJUSTMENT_ATTRIBUTION_UNKNOWN',
  /** The reconciliation named adjustments but no document to reconcile them against. */
  'RECON_V2_ADJUSTMENT_WITHOUT_TARGET',
  /**
   * The accounting posting refused after the reconciliation was written, so the reconciliation
   * was reversed. Bank and ledger must never be allowed to diverge silently (D-S5b).
   */
  'RECON_V2_GL_POSTING_FAILED',
  /**
   * The reconciliation was written but could not be reversed after the posting refused. The
   * movement needs a person: reported rather than hidden behind a generic failure.
   */
  'RECON_V2_GL_POSTING_FAILED_DIRTY',
  /** The movement changed state between being read and being written. The caller re-reads. */
  'RECON_V2_STATUS_CHANGED',
  /** A batch item's proposal legs did not add up to the movement, so it was not confirmed. */
  'RECON_V2_BATCH_ITEM_AMBIGUOUS_ALLOCATION',
  /** A document the request named does not exist for this tenant, or is out of the caller's scope. */
  'RECON_V2_TARGET_NOT_FOUND',
  /**
   * Charlie r6b Q1: an AR target's instalment breakdown does not describe open parcels of that
   * invoice adding up to the target's amount (a parcel named twice, of another invoice, already
   * settled, or no longer open for the amount named). Nothing was written.
   */
  'RECON_V2_AR_SCHEDULE_ALLOCATION_INVALID',
  // --- ALC-4243 review round 2 ---
  /**
   * v1's `AP_SETTLEMENT_ADJUSTMENT_UNASSIGNED`: an operational adjustment on a settlement group
   * must name the payment it affects, and this one could not be attributed.
   */
  'RECON_V2_AP_ADJUSTMENT_UNASSIGNED',
  /** v1's `AP_SETTLEMENT_REJECTED`: the AP settlement itself refused the reconciliation. */
  'RECON_V2_AP_SETTLEMENT_REJECTED',
  /** `allocatedAmountExact` / `amountExact` is malformed, or disagrees with its own float. */
  'RECON_V2_AMOUNT_EXACT_INVALID',
  // --- ALC-4243 review round 4 ---
  /**
   * Create-and-reconcile was refused by v1 for a reason that is not the ledger's (an AP
   * reconcile refusing the cash, a relabel failing), and what it had created was taken back.
   */
  'RECON_V2_CREATE_AND_RECONCILE_REFUSED',
  'RECON_V2_AP_SETTLEMENT_RETRY',
  // --- ALC-4325 (internal operation S1): the pick's pre-flight. Each refusal writes nothing. ---
  /** Step 0 (Q3): v1 covers BR_CPC books in BRL only. `{frameworkCode, functionalCurrency, lineCurrency}` */
  'RECON_V2_INTERNAL_OP_BOOK_NOT_SUPPORTED',
  /** Aplicação against a CREDIT, Resgate against a DEBIT, or a pick the account cannot take. `{expectedDirection, actualDirection}` */
  'RECON_V2_INTERNAL_OP_DIRECTION_MISMATCH',
  /** An Aplicação or Resgate on a checking line names no INVESTMENT account. `{}` */
  'RECON_V2_INTERNAL_OP_INVESTMENT_ACCOUNT_REQUIRED',
  /** The named account is not an ACTIVE INVESTMENT account of the same entity. `{counterpartAccountId}` */
  'RECON_V2_INTERNAL_OP_COUNTERPART_INVALID',
  /** The INVESTMENT account has no TREASURY_BANK_ACCOUNT GL binding at the posting version. `{bankAccountId, bankAccountName}` */
  'RECON_V2_INTERNAL_OP_INVESTMENT_ACCOUNT_GL_NOT_CONFIGURED',
  /** The two accounts do not carry the same confirmed currency. `{lineCurrency, investmentCurrency}` */
  'RECON_V2_INTERNAL_OP_CURRENCY_MISMATCH',
  /** A sweep account's line with neither a partner movement nor a named INVESTMENT account. `{bankAccountId}` */
  'RECON_V2_INTERNAL_OP_SWEEP_ACCOUNT_NEEDS_PAIR',
  // --- ALC-4323 (S0b): "Fim do histórico importado". Literals, for the TDZ reason above. ---
  /**
   * 409 — FEE_BILL / VENDOR_ADVANCE_BILL on a movement dated on or before the entity's
   * historical ledger end date: the imported razão already has it, so a bill would book it
   * twice. `details.historicalLedgerEndDate` names the date. Nothing is created.
   */
  'RECON_V2_HISTORICAL_LEDGER_PERIOD_DOCUMENT_BLOCKED',
  /** 409 — HISTORICAL_LEDGER on a movement dated after the entity's historical ledger end date. */
  'RECON_V2_HISTORICAL_LEDGER_OUTSIDE_PERIOD',
  /** 409 — HISTORICAL_LEDGER for an entity with no historical ledger end date. `details.entityId`. */
  'RECON_V2_HISTORICAL_LEDGER_END_DATE_NOT_SET',
  /**
   * 409 — the movement is on or before the end date, but its bank account was not in the tie-out
   * the date was saved with (added after the save, or never proven). HISTORICAL_LEDGER and the
   * document kinds are both refused until the date is saved again with the account tied out.
   * `details.historicalLedgerEndDate`, `details.bankAccountId`.
   */
  'RECON_V2_HISTORICAL_LEDGER_ACCOUNT_NOT_TIED_OUT',
  // --- ALC-4326 (internal operation S2): route (b), the created counterpart. Literals. ---
  /**
   * 400 — v1's "reconcile as internal operation" for an Aplicação or a Resgate. Those are posted
   * only by the new reconciliation (v2), which creates the investment-side movement. `{}`.
   */
  'RECON_V2_INTERNAL_OP_USE_V2',
  /**
   * 400 — ALC-4326 round 1 (6(a)): "Criar transferência" (INTERNAL_TRANSFER) between two accounts
   * that do not carry the same confirmed currency. Nothing is written. The message text is the one
   * the operation always answered with; only the code is new.
   * `{lineCurrency, counterpartCurrency, counterpartAccountId}`.
   */
  'RECON_V2_TRANSFER_CURRENCY_MISMATCH',
  /**
   * 400 — ALC-4326 round 1 (N3): an INTERNAL_OPERATION request that names no `operationType`, on a
   * movement after the end of the imported history. Nothing is written; the user picks the type
   * (Aplicação, Resgate, Rendimento, Imposto retido). Inside the history the request still reconciles
   * with no journal. `{bankAccountId, lineOperationType}` (the importer's type, or null).
   */
  'RECON_V2_INTERNAL_OP_PICK_REQUIRED',
  /**
   * 400 — ALC-4326 round 1 (N2): a Resgate (route (b)) on an investment account whose GL balance
   * ("saldo contábil") is 0,00 or below at the line's day. Nothing is written. The balance is not
   * the Resgate's fault: the opening balance is usually missing. `{investmentAccountId, balance,
   * balanceDate, amount}`.
   */
  'RECON_V2_INTERNAL_OP_INVESTMENT_GL_BALANCE_NOT_POSITIVE',
  // --- ALC-4328 (internal op S2b): the Resgate split. Literals. ---
  /**
   * 409 — a Resgate dated before another Resgate already posted on the same investment account
   * (a journal crediting its bound GL account, or a split journal naming one of its legs, and not
   * reversed). Taking principal out of an earlier day would change the later Resgate's split.
   * Nothing is written. `{investmentAccountId, laterTransactionId, laterDate, amount}`.
   */
  'RECON_V2_INTERNAL_OP_RESGATE_OUT_OF_ORDER',
  /**
   * 409 — a WARNING, not a refusal of the request: the Resgate has income above the carrying amount
   * (E > 0) and no IRRF was typed or sent by the bank. Nothing is written. Retry the same request
   * (same idempotency key) with `acknowledgeWarnings: ['RECON_V2_INTERNAL_OP_RESGATE_IRRF_BLANK']`
   * to post it with IRRF 0,00, or with `withheldTaxAmount`. `{investmentAccountId, excessAmount,
   * principalAmount, carryingAmount, amount, requiresAcknowledgement}`.
   */
  'RECON_V2_INTERNAL_OP_RESGATE_IRRF_BLANK',
  /**
   * 409 — ALC-4328 round 2 (O7): an Aplicação dated on or before a split Resgate (one booked with
   * financial income, INVESTMENT_REDEMPTION_GAIN) already posted on the same investment account
   * and not reversed. That Resgate's split was read on the balance without this Aplicação, so the
   * Resgate is undone first, the Aplicação posted, and the Resgate picked again. Same day counts.
   * Nothing is written. `{investmentAccountId, laterTransactionId, laterDate, amount}`.
   */
  'RECON_V2_INTERNAL_OP_APLICACAO_BEFORE_POSTED_RESGATE',
  /**
   * 409 — ALC-4329 (S3-AC3): the line's mirror movement is already on the investment account
   * (PENDING, opposite direction, equal cents, within ±3 days), so a created counterpart would
   * post the operation twice. Nothing is written; pair with that movement instead.
   * `{candidateTransactionId, pairKind}`.
   */
  'RECON_V2_INTERNAL_OP_PAIR_AVAILABLE',
  /**
   * 409 — ALC-4329 (S3-AC3): this investment-side movement was already booked from the checking
   * side (a route (b) created leg stands in for it). Nothing is written. `{groupId}`.
   */
  'RECON_V2_INTERNAL_OP_ALREADY_RECLASSIFIED',
  /**
   * 409 — ALC-4329 round 1 (P3): a Resgate from the checking side whose investment account takes
   * route (a) (it has a feed) but has no mirror movement anywhere in the ±3 São Paulo-day window,
   * so there is nothing to pair with and a created counterpart would be booked twice once the feed
   * brings it. Nothing is written. Import the investment account's statement, or book it through
   * accounting. `{investmentAccountId, investmentAccountName, amount, windowDays}`.
   */
  'RECON_V2_INTERNAL_OP_ROUTE_A_MIRROR_MISSING',
  /**
   * 400 — ALC-4329 round 2 (P5) + round 3 (P5a): a route (c) sweep pair (same account, no
   * journal) whose two halves sit in different months in EITHER reading: the balance proof's
   * month (UTC month of the stored instant) or the line's calendar day (stored date of a
   * date-only line, São Paulo day otherwise). E.g. −140,83 on 31/08 and +140,83 on 01/09.
   * Route (c) writes no journal, so each month's statement would keep a movement no journal of
   * that month explains. Nothing is written. Book it as an Aplicação and a Resgate on the
   * sweep's investment account. `{bankAccountId, lineDate, partnerDate, linePeriod,
   * partnerPeriod}` (calendar days `YYYY-MM-DD`; proof months `YYYY-MM`).
   */
  'RECON_V2_INTERNAL_OP_SWEEP_CROSSES_MONTH',
  /**
   * 409 — ALC-4359 round 1 (backend MAJOR-1): the movement's bank account was deleted (soft delete,
   * `deleted_at` set) before this write took the line's lock. A reconcile that queued behind the
   * delete must not post GL onto a hidden account, so it is refused once it holds the line lock.
   * Nothing is written, and the user cannot fix it by retrying: the account is gone, so the screen
   * re-reads. `{bankAccountIds}`.
   */
  'RECON_V2_BANK_ACCOUNT_DELETED',
  // --- ALC-4358 (internal op, OQ7 A): "Imposto retido definitivo". Literal. ---
  /**
   * 400 — the "Imposto retido definitivo" pick (`WITHHOLDING_TAX_FINAL`) on a line whose entity
   * book has no account mapped to the `IRRF_DEFINITIVO` tax slot ("IRRF Definitivo – Outras Operações") at the
   * posting period's chart version. Pre-flight: nothing is written (0 groups, 0 legs, 0 journals,
   * 0 posting rows, 0 audit rows, the pick unchanged) and no GL account is created. The tenant
   * maps it once on the Fiscal tab of Configurações → Contabilidade e Fiscal → Mapeamento de
   * contas. `{slotCode, slotName, entityId}`.
   */
  'RECON_V2_INTERNAL_OP_IRRF_FINAL_SLOT_NOT_MAPPED',
] as const;

export type ReconV2ErrorCode = (typeof RECON_V2_ERROR_CODES)[number];

/**
 * The queue, split by where the movements sit relative to the period (decision D-S2b).
 *
 * `pendingSignedSum` is the standard bank-reconciliation figure: EVERY unreconciled movement
 * dated on or before the period end, not only this month's. An unreconciled payment from
 * March still explains part of an August difference, and leaving it out would surface it as
 * an unexplained residual and refuse a close that is in fact square. The split exists so the
 * screen can say "R$ X de meses anteriores" instead of presenting one opaque number.
 */
export interface ReconV2PendingBreakdown {
  /** Signed sum of queued movements dated inside the period. Functional currency, null with no rate. */
  thisPeriod: string | null;
  /** Signed sum of queued movements dated before the period start. Functional currency, null with no rate. */
  earlierPeriods: string | null;
  /** Movement counts behind each figure. */
  thisPeriodCount: number;
  earlierPeriodsCount: number;
}

/**
 * The lines the provider removed that the ERP kept with active posted GL (ALC-4363), split by where
 * they sit relative to the period, like `ReconV2PendingBreakdown`. Each figure is the excess of the
 * statement over the ledger that the lines cause: see `providerRemovedActiveGlSum`.
 */
export interface ReconV2ProviderRemovedBreakdown {
  /** Lines dated inside the period. Functional currency, null with no rate. */
  thisPeriod: string | null;
  /** Lines dated before the period start. Functional currency, null with no rate. */
  earlierPeriods: string | null;
  /** Line counts behind each figure. */
  thisPeriodCount: number;
  earlierPeriodsCount: number;
}

/** How many removed lines the balance proof names; the counts in the breakdown are complete. */
export const RECON_V2_PROVIDER_REMOVED_PROOF_LINE_CAP = 100;

/**
 * The part of the balance proof that names bank lines the provider removed but the ERP kept
 * (ALC-4363). It sits on `ReconV2BalanceProof` itself, not on the additions below, because the web
 * reads it: the DIFERENÇA reason and the close blocker.
 */
export interface ReconV2ProviderRemovedProofFacts {
  /**
   * ALC-4363 (decision ORCH Q11 = A): how far the statement stands ABOVE the ledger because of
   * bank lines the provider removed that the ERP kept, still carrying active posted GL; functional
   * currency, cumulative to the period end. The bank no longer has the line, the GL does: a removed
   * debit of R$ 140,83 gives `'140.83'` (the ledger still holds the payment). It is the fourth term
   * of the identity, named so a difference the bank caused is not shown as unexplained, and it
   * blocks close (`PROVIDER_REMOVED_ACTIVE_GL`). Zero when there is none. Null when the
   * proof is unavailable, or when a foreign-currency account has no CLOSING rate (no functional
   * figure at all, like `pendingSignedSum`).
   */
  providerRemovedActiveGlSum: string | null;
  /** `providerRemovedActiveGlSum` split by period, with counts. Null when the proof is unavailable. */
  providerRemovedBreakdown: ReconV2ProviderRemovedBreakdown | null;
  /**
   * The lines behind `providerRemovedActiveGlSum`, oldest first, capped at
   * `RECON_V2_PROVIDER_REMOVED_PROOF_LINE_CAP` (the counts in the breakdown are never capped;
   * `providerRemovedLinesTruncated` says whether the cap cut the list).
   * Cumulative to the period end, like the sum, so a reader who is not the queue (an agent over
   * MCP) can name the lines that block the close. The undo state is the queue's to carry. Empty
   * when there is none or the proof is unavailable.
   */
  providerRemovedLines: ReconV2ProviderRemovedProofLine[];
  /**
   * True when more lines back `providerRemovedActiveGlSum` than `providerRemovedLines` carries
   * (the cap cut the list; the sum and the breakdown counts stay complete). False when the list is
   * whole, and when the proof is unavailable. Explicit, like `ReconV2QueueResponse.truncated`, so
   * a consumer does not have to derive it from the counts.
   */
  providerRemovedLinesTruncated: boolean;
}

/** Fields added to `ReconV2BalanceProof` by ALC-4237; merged into the response object. */
export interface ReconV2BalanceProofAdditions {
  /** `pendingSignedSum` split by period. Null when the proof is unavailable. */
  pendingBreakdown: ReconV2PendingBreakdown | null;
  /**
   * Names of the other bank accounts sharing this account's GL account. Non-empty only with
   * `unavailableReason = 'LEDGER_SHARED_GL_ACCOUNT'`.
   */
  sharedLedgerAccountNames: string[];
  /**
   * True while the period has not ended yet. The proof still evaluates (at today's date), but
   * a month in progress must not be closed — ALC-4242 refuses on this flag rather than
   * re-deriving the calendar.
   */
  periodInProgress: boolean;
}

/**
 * Compile-time exhaustiveness: every member of each union must appear in its value array.
 * `satisfies` above already rejects a value the union does not allow; these reject the other
 * direction, so adding a union member without listing it fails the build instead of silently
 * dropping it from a DTO validator or an MCP enum.
 */
type AssertNever<T extends never> = T;
export type _ReconV2AccountKindsExhaustive = AssertNever<
  Exclude<ReconV2AccountKind, (typeof RECON_V2_ACCOUNT_KINDS)[number]>
>;
export type _ReconV2AccountStatesExhaustive = AssertNever<
  Exclude<ReconV2AccountState, (typeof RECON_V2_ACCOUNT_STATES)[number]>
>;
export type _ReconV2StatementBalanceSourcesExhaustive = AssertNever<
  Exclude<ReconV2StatementBalanceSource, (typeof RECON_V2_STATEMENT_BALANCE_SOURCES)[number]>
>;
export type _ReconV2ProofBlockersExhaustive = AssertNever<
  Exclude<ReconV2ProofBlocker, (typeof RECON_V2_PROOF_BLOCKERS)[number]>
>;
export type _ReconV2SourcesExhaustive = AssertNever<
  Exclude<ReconV2Source, (typeof RECON_V2_SOURCES)[number]>
>;
export type _ReconV2PeriodStatusKindsExhaustive = AssertNever<
  Exclude<ReconV2PeriodStatusKind, (typeof RECON_V2_PERIOD_STATUS_KINDS)[number]>
>;

// ---------------------------------------------------------------------------
// --- ALC-4243 additions ---
// The write actions of the decision queue: reconcile, batch confirm, ignore/restore and
// create-and-reconcile. Purely additive except for two in-place edits recorded in the hand-off:
// `ReconV2ReconcileRequest.adjustments` was widened to `ReconV2AdjustmentInput[]` (the shape
// ALC-4241 documented), and this slice's codes were appended to `RECON_V2_ERROR_CODES`.
// ---------------------------------------------------------------------------

/**
 * Hard cap on `ReconV2BatchConfirmRequest.transactionIds`.
 *
 * Each item is its own group and its own database transaction, so the batch is a loop and not a
 * single unit of work: the cap bounds how long one request can hold a connection, not how much
 * can be undone. 100 is the screen's own ceiling — the queue returns at most
 * `RECON_V2_QUEUE_ITEM_CAP` rows and "Confirmar todos" acts on one decision group of them — and
 * it keeps the worst case (100 sequential confirmations, each posting a journal) inside the
 * API's request timeout.
 */
export const RECON_V2_BATCH_CONFIRM_MAX = 100;

/** Max length of `ReconV2IgnoreRequest.reason`, matching `op_ignored_transaction.reason`. */
export const RECON_V2_IGNORE_REASON_MAX_LENGTH = 200;

/**
 * A row of the "Ignorados" tab.
 *
 * Declared here to the exact shape the web already consumes
 * (`apps/web/src/components/features/reconciliation-v2/data/data-source.ts`), which had to
 * declare it locally because the contract carried no listing. The web's local copy stays as it
 * is — this slice does not touch the screen — but the API answers this shape, so the two can be
 * folded into one import without a behaviour change.
 */
export interface ReconV2IgnoredItem {
  transactionId: string;
  accountId: string;
  postedAt: string;
  description: string;
  counterpartyName: string | null;
  amount: string;
  currencyCode: string;
  /** The reason the user gave, trimmed. Never blank: an ignore without one is refused. */
  reason: string;
  ignoredAt: string;
  /** Who set it aside. Null when the audit row names an actor this tenant can no longer resolve. */
  ignoredByName?: string | null;
}

/** GET /reconciliation/v2/ignored?accountId=&period= */
export interface ReconV2IgnoredResponse {
  items: ReconV2IgnoredItem[];
}

/** POST /reconciliation/v2/transactions/:transactionId/ignore */
export interface ReconV2IgnoreResponse {
  transactionId: string;
  status: 'IGNORED';
  reason: string;
  ignoredAt: string;
}

/** DELETE /reconciliation/v2/transactions/:transactionId/ignore — back into the queue. */
export interface ReconV2RestoreResponse {
  transactionId: string;
  status: 'PENDING';
  restoredAt: string;
}

/**
 * The document kinds "Criar e conciliar" can create.
 *
 * Exactly the workbench's own set (`ReviewCreateMenuConfig` in
 * `apps/web/src/components/features/reconciliation/review/ReviewCreateMenu.tsx`), restricted to
 * the four that have a server-side create-and-reconcile service behind them. No new document
 * kind is invented here: each maps 1:1 onto a method the workbench already calls.
 */
export const RECON_V2_CREATE_KINDS = [
  /** A BANK_FEE AP bill for a tariff the statement charged. `reconcileBankFeeBillFromWorkbench`. */
  'FEE_BILL',
  /** A VENDOR_ADVANCEMENT bill with no originating AP bill. */
  'VENDOR_ADVANCE_BILL',
  /**
   * The missing counterpart of an internal transfer: the movement exists on one account and the
   * other account never imported its side. This is the workbench's "Criar transferência", which
   * CREATES the opposite leg — not the pairing of two movements that both already exist, which
   * the Identificar workbench handles as an ordinary `BANK_TX` target.
   */
  'INTERNAL_TRANSFER',
  /** An internal operation (application, redemption, yield) on the same account. */
  'INTERNAL_OPERATION',
  /**
   * ALC-4323 (S0b): "Já contabilizado no histórico importado". The movement is dated on or
   * before the entity's historical ledger end date, so the imported razão already booked it:
   * reconciled against itself, no document, no journal.
   */
  'HISTORICAL_LEDGER',
] as const;
export type ReconV2CreateKind = (typeof RECON_V2_CREATE_KINDS)[number];

/** POST /reconciliation/v2/transactions/:transactionId/create-and-reconcile */
export type ReconV2CreateAndReconcileRequest =
  | {
      kind: 'FEE_BILL';
      /** The vendor to bill the tariff to. Omitted: the tenant's default bank-fee vendor. */
      vendorId?: string;
      description?: string;
    }
  | {
      kind: 'VENDOR_ADVANCE_BILL';
      vendorId: string;
      description?: string;
    }
  | {
      kind: 'INTERNAL_TRANSFER';
      /**
       * The entity's other account, the one whose side of the transfer is missing. Which of the
       * two is origin and which is destination follows the movement's own direction, so the
       * caller never has to state it and cannot state it wrongly.
       */
      counterpartAccountId: string;
      description?: string;
    }
  | {
      kind: 'INTERNAL_OPERATION';
      /**
       * ALC-4325 (S1-AC6): the user's pick — `YIELD`, `APPLICATION`, `RESCUE` (resgate),
       * `WITHHOLDING_TAX` (IRRF retido, compensável) or `WITHHOLDING_TAX_FINAL` (ALC-4358: IRRF
       * retido como tributação definitiva, não compensável). It wins over the importer's guess and is stored on the
       * line once the posting succeeds. Legacy API callers may omit it (the DTO allows that); the
       * line's importer type is then used, after the same book/currency pre-flight.
       */
      operationType: ReconV2InternalOperationPick;
      /**
       * APPLICATION / RESCUE booked from the checking (or sweep) side: the INVESTMENT account
       * that holds the position. From an INVESTMENT line: optionally the checking account.
       */
      counterpartAccountId?: string;
      /** A sweep account's line: the other statement line of the same account it pairs with. */
      sweepPartnerTransactionId?: string;
      /** RESCUE only: IRRF withheld on the redemption, as a non-negative decimal string. */
      withheldTaxAmount?: string;
      /** Warning codes the user has seen and accepted (warn + consent; a warning never blocks). */
      acknowledgeWarnings?: string[];
    }
  | {
      /** ALC-4323: already booked by the imported razão. No other field. */
      kind: 'HISTORICAL_LEDGER';
    };

/**
 * The result of a create-and-reconcile. Shaped as the reconcile response plus what was created,
 * so the screen can link to the new document without a second round trip.
 */
export interface ReconV2CreateAndReconcileResponse {
  groupId: string;
  transactionIds: string[];
  journalIds: string[];
  created: {
    kind: ReconV2CreateKind;
    /** The created document's id — a bill id, or the paired movement's id for a transfer. */
    documentId: string | null;
    href: string | null;
  };
  /**
   * ALC-4328 round 2 (O8 m1): non-blocking notices about a pick that WAS saved. Absent when there
   * is none. Never a refusal and never a warning that needs consent.
   */
  notices?: ReconV2CreateNotice[];
}

/**
 * ALC-4328 round 2 (O8 m1): a notice on a saved pick.
 *
 * `RECON_V2_INTERNAL_OP_RESGATE_RAZAO_NO_CREDIT` — a route (b) Resgate inside the imported history
 * on a day the Razão credits nothing to the investment account's GL (it booked the Resgate
 * elsewhere, e.g. straight to income). Nothing posts and no excess leg is created, so the
 * investment account's movements drop by the Resgate while its GL keeps the amount: e.g.
 * movements 0,00 while the GL shows 50.000,00. `{investmentAccountId, date, amount}`.
 */
export interface ReconV2CreateNotice {
  code: 'RECON_V2_INTERNAL_OP_RESGATE_RAZAO_NO_CREDIT';
  /** pt-BR copy. */
  message: string;
  details: { investmentAccountId: string; date: string; amount: string };
}

/**
 * Fields added to `ReconV2ReconcileResponse` by ALC-4243; merged into the response object.
 *
 * `skippedAdjustments` is how criterion D-S5b stays visible: an AP withholding the bill already
 * booked produces no journal and no error, and without this the screen would show a
 * reconciliation that silently dropped a line the user typed.
 */
export interface ReconV2ReconcileResponseAdditions {
  skippedAdjustments: {
    type: ReconV2AdjustmentType;
    reason: ReconV2AdjustmentSkipReason;
    /** The part of the amount that produced no journal, exact decimal string. */
    skippedAmount: string;
  }[];
  /** True when this response was replayed from the idempotency record rather than written now. */
  replayed: boolean;
}

/**
 * Compile-time exhaustiveness for this slice's runtime tuple, in both directions — the same
 * `AssertNever` pattern ALC-4237 and ALC-4240 use.
 */
/** A request variant whose `kind` is not in the tuple fails to compile. */
export type _ReconV2CreateRequestKindsListed = AssertNever<
  Exclude<ReconV2CreateAndReconcileRequest['kind'], ReconV2CreateKind>
>;
/** And the other direction: a listed kind with no request variant behind it fails too. */
export type _ReconV2CreateKindsHaveRequests = AssertNever<
  Exclude<ReconV2CreateKind, ReconV2CreateAndReconcileRequest['kind']>
>;

// --- ALC-4242 additions ---
//
// Statement close / reopen. The contract above already declares the close and reopen
// request/response shapes; what is added here is the second close route (decision D-S6a) and
// the vocabulary the shared "is this month closed?" seam answers with.

/**
 * `POST /reconciliation/v2/accounts/:accountId/close?period=YYYY-MM` — decision D-S6a.
 *
 * Many accounts never receive a `BankStatement` row for the month: an Open Finance connection
 * that only reports daily balances leaves `ReconV2AccountTab.statementId` null, so the
 * statement route cannot be reached at all. This route takes the account and the period
 * instead, finds that month's statement or creates it inside the same transaction, and then
 * runs exactly the same proof gate and the same close as the statement route.
 *
 * Same permission (`reconciliation:close`) and the same response
 * ({@link ReconV2CloseStatementResponse}) as the statement route.
 */
export interface ReconV2CloseAccountQuery {
  /** `YYYY-MM`. */
  period: string;
}

/** Response of the account close route — identical to the statement route's. */
export type ReconV2CloseAccountResponse = ReconV2CloseStatementResponse;

/**
 * Details carried by `RECON_V2_STATEMENT_NOT_CLOSABLE` so the caller can render the same
 * "o que falta para fechar" list the proof panel shows, without a second round trip.
 */
export interface ReconV2StatementNotClosableDetails {
  blockers: ReconV2ProofBlocker[];
}

// --- ALC-4242 round 3, item 8: what the close dialog has to be able to say ---

/**
 * Fields added to `ReconV2BalanceProof` by ALC-4242 round 3; merged into the response object.
 *
 * Both answer a question the close dialog cannot answer from the proof's numbers alone, and
 * both are about the STATEMENT rather than about the arithmetic — which is why they live here
 * and not among the blockers: neither refuses a close, they change what the user is told they
 * are agreeing to.
 */
export interface ReconV2BalanceProofStatementFacts {
  /**
   * Whether a `BankStatement` row actually covers this account-month (item 8c).
   *
   * `false` is the ordinary case for an Open Finance account that only reports daily
   * balances: the close still works — decision D-S6a creates the row — but it is stamping a
   * closing balance the bank never sent as a statement, and the dialog says so before the
   * user commits to it ("Não há extrato importado para agosto. Ao fechar, o sistema registra
   * o saldo de R$ … em 31/08").
   */
  hasStatement: boolean;

  /**
   * Movements that ARRIVED in this month after it was closed (item 8d).
   *
   * The Open Finance sync never drops bank data, so a late movement is stored and the closed
   * month's balances are left alone. That is the right behaviour and it is also invisible:
   * the account reads CLOSED and the queue shows rows nobody accounted for. This is what lets
   * the screen say "2 movimentos chegaram depois do fechamento" and offer the reopen.
   *
   * ARRIVED, strictly: a row created after the close. A row that was already there and has
   * since been WRITTEN to — a rejected suggestion, a provider re-reading its history — is not
   * counted, because the sentence this feeds says movements came in (round 5, N1). A provider
   * touching a closed month is reported separately, as a warning from the path that did it.
   *
   * `null` when the month is not closed — the question does not apply then.
   */
  lateMovements: ReconV2LateMovementSummary | null;
}

/** Counts only; naming the movements is the queue's job, under the reader's own scope. */
export interface ReconV2LateMovementSummary {
  /** How many movements of this account-month were CREATED after its close. */
  count: number;
  /** The most recent one's `postedAt`, ISO-8601, or null when none is dated. */
  latestPostedAt: string | null;
  /** When the last of them arrived (its `createdAt`), ISO-8601. */
  latestObservedAt: string | null;
}

/** ALC-4325 (S1-AC6): the internal-operation types a user can pick on a statement line. */
export const RECON_V2_INTERNAL_OPERATION_PICKS = [
  'YIELD',
  'APPLICATION',
  'RESCUE',
  'WITHHOLDING_TAX',
  // ALC-4358 (OQ7 A): "Imposto retido definitivo", IRRF as a final tax (slot IRRF_DEFINITIVO).
  'WITHHOLDING_TAX_FINAL',
] as const;

export type ReconV2InternalOperationPick = (typeof RECON_V2_INTERNAL_OPERATION_PICKS)[number];

/**
 * ALC-4329 round 1 (F4): the route the server takes for an APPLICATION / RESCUE (or any pick) on
 * one statement line — `GET /reconciliation/v2/transactions/:transactionId/internal-operation-route`
 * (alias `items/:transactionId/…`), query `operationType`, `counterpartAccountId`,
 * `sweepPartnerTransactionId`. `op:manage`, read-only: it runs the write path's own pre-flight
 * (`evaluateInternalOperationPreflight`) and route (a) pairing rule
 * (`resolveInternalOperationRouteAPairing`), never a second copy, and writes nothing.
 *
 * It answers WHICH route, not WHETHER the posting will succeed: the Resgate split refusals
 * (OUT_OF_ORDER, NOT_POSITIVE), the IRRF consent, missing slots and the O7 Aplicação guard are
 * decided only by the write.
 */
export type ReconV2InternalOperationRoute =
  'FEED_MIRROR' | 'CREATED_COUNTERPART' | 'SAME_ACCOUNT_SWEEP';

export interface ReconV2InternalOperationRouteResponse {
  /**
   * (a) FEED_MIRROR — the investment account's feed holds (or will hold) the other half;
   * (b) CREATED_COUNTERPART — the investment leg is created; (c) SAME_ACCOUNT_SWEEP — two lines
   * of one flagged account, no journal. Null for a YIELD / WITHHOLDING_TAX(_FINAL) pick, a legacy
   * request, or a refusal.
   */
  route: ReconV2InternalOperationRoute | null;
  /**
   * Route (a) only. SAME_DAY: pairs `mirrorTransactionId` now. AWAITING: an Aplicação with no
   * same-day mirror waits for its pair (transit leg). COMPLETES_AWAITING: this line completes
   * the awaiting group `awaitingGroupId`. Null otherwise (and inside the imported history, where
   * the line settles SKIPPED with no journal).
   */
  pairing: 'SAME_DAY' | 'AWAITING' | 'COMPLETES_AWAITING' | null;
  mirrorTransactionId: string | null;
  awaitingGroupId: string | null;
  /** The line is on or before its entity's "Fim do histórico importado": nothing posts. */
  insideHistoricalLedger: boolean;
  /**
   * The refusal the write would answer before any write, with the same stable code:
   * a pre-flight refusal (`details`), `RECON_V2_CREATE_INPUT_INVALID` (`details.field`,
   * `details.detail`), `RECON_V2_INTERNAL_OP_ROUTE_A_MIRROR_MISSING`,
   * `RECON_V2_INTERNAL_OP_ALREADY_RECLASSIFIED`, or `RECON_V2_GL_POSTING_FAILED` with
   * `reasons` (the cross-day Resgate). Null when the route is open.
   */
  refusal: {
    code: string;
    details: Record<string, unknown> | null;
    reasons: string[] | null;
  } | null;
}
